/**
 * The M2 bridge's contracts (M3 plan §7 item 4, slice R22g; the career in the Reach only): once the career feature
 * (order 150) has provided `m2`, binds the Reach's lane-repair, relay-parts and chart-a-system targets into M2's
 * contract generator and marks those jobs ready from the Reach events (`src/bridge/m2/contracts.ts`, loaded lazily
 * with the Reach). The per-frame step reads the Reach event ring and allocates nothing without a matching event.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { CareerAccess } from '../../bridge/m2/career'
import type { ReachContracts } from '../../bridge/m2/contracts'
import type { CareerService } from '../../career/flow'
import type { M2Runtime } from '../../space/contracts'

/** After the Reach feature (160) and the bridge scan (161). */
const ORDER = FEATURE_ORDER.reach + 2

let contracts: ReachContracts | null = null

/** The `m2` service with its dev-command write hook (the career flow's `CareerService`). */
function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

const feature: GameFeature = {
  name: 'bridgeContracts',
  order: ORDER,
  async init(ctx: GameContext): Promise<void> {
    const reach = ctx.reach
    const m2 = ctx.services.maybe('m2')
    if (reach === null || m2 === undefined || !hasWriteHook(m2)) return
    const [{ attachReachContracts }, { careerAccess }] = await Promise.all([import('../../bridge/m2/contracts'), import('../../bridge/m2/career')])
    const access: CareerAccess = careerAccess(m2)
    contracts = attachReachContracts(reach, () => access)
  },
  frame(): void {
    contracts?.step()
  },
  dispose(): void {
    contracts?.dispose()
    contracts = null
  },
}

export default feature
